import { InformationRequestStatus } from "@tfg/db"
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { Navigate } from "react-router-dom"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { TraceabilityResponses } from "@tfg/types/endpoints/traceability"

import {
  OperationItem,
  RequestWithOpItem,
} from "~/pages/SupplierPortal/TraceabilityRequests/utils"
import { useAuth } from "~/store/auth-context"

type InformationRequestContextType = {
  user: LibraryResponses.TFGUser
  isAdmin: boolean
  canSubmitIR: boolean
  request: RequestWithOpItem
  status: InformationRequestStatus
  currentContactId: number
  progressByOperationId: Record<number, number>
  customFieldValues: TraceabilityResponses.CustomFieldValue[]
  setCustomFieldValues: Dispatch<
    SetStateAction<TraceabilityResponses.CustomFieldValue[]>
  >
  setProgress: (operationId: number, value: number) => void
}

type InformationRequestContextProps = {
  children: ReactNode
  request: RequestWithOpItem
  status: InformationRequestStatus
}

const InformationRequestContext = createContext<
  InformationRequestContextType | undefined
>(undefined)

export function InformationRequestContextProvider({
  children,
  request,
  status,
}: InformationRequestContextProps) {
  const { user, isAdmin } = useAuth()
  const [progressByOperationId, setProgressByOperationId] = useState<
    Record<number, number>
  >(
    request.opItems.reduce(
      (acc, item) => {
        if (!item.operation) {
          return acc
        }
        acc[item.operation.id] = item.operationCompletionStatus?.progress ?? 0
        return acc
      },
      {} as Record<number, number>
    )
  )
  const currentContactId = request.original.contactId

  const setProgress = useCallback(
    (operationId: number, value: number) => {
      setProgressByOperationId((prev) => ({
        ...prev,
        [operationId]: value,
      }))
    },
    [setProgressByOperationId]
  )
  const [customFieldValues, setCustomFieldValues] = useState<
    TraceabilityResponses.CustomFieldValue[]
  >(
    request.opItems.flatMap((item) => {
      if (!item.operation) {
        return []
      }
      return item.operation.customFieldsValues
    })
  )

  useEffect(() => {
    setCustomFieldValues(
      request.opItems.flatMap((item) => {
        if (!item.operation) {
          return []
        }
        return item.operation.customFieldsValues
      })
    )
  }, [request.opItems])

  if (!user) {
    return <Navigate to="/login" replace={true} />
  }

  const canSubmitIR = Object.values(progressByOperationId).every(
    (progress) => progress === 100
  )

  const value: InformationRequestContextType = {
    user,
    isAdmin,
    request,
    status,
    currentContactId,
    canSubmitIR,
    customFieldValues,
    setCustomFieldValues,
    setProgress,
    progressByOperationId,
  }

  return (
    <InformationRequestContext.Provider value={value}>
      {children}
    </InformationRequestContext.Provider>
  )
}

export function useInformationRequestContext() {
  const context = useContext(InformationRequestContext)
  if (!context) {
    throw new Error(
      "useInformationRequestContext must be used within an InformationRequestContextProvider"
    )
  }
  return context
}

export const getOperationItemIsTransferred = (
  operationItem: OperationItem,
  operationOwnerId: string | undefined
) => {
  const contact = operationItem.operation?.contact
  if (!contact) {
    return false
  }
  if (contact.userId !== operationOwnerId) {
    return true
  }
  return false
}
